import React, {Component} from 'react';
import classNames from 'classnames';
import {DropdownButton, MenuItem, OverlayTrigger, Tooltip} from 'react-bootstrap';
import {observer, inject} from 'mobx-react';

import EditCaption from 'js/components/edit_caption';
import {toTitleCase} from 'js/common/string_helper';
import ManualDistributionModal from 'js/components/manual_distribution_modal';
import ActivityFeedList from 'js/approval_queues/components/activity_feed/activity_feed_list';

import AddDistributionModal from './add_distribution_modal';
import AddStoryDistributionModal from './add_story_distribution_modal';
import {AddFeedbackForm, StatusTimelineItem} from './helpers';
import Tag from 'js/components/tag';

const ActivityFeedState = {
    SHOW_FEED: 'show_feed',
    SHOW_REQUEST_FIX_FORM: 'show_request_fix',
    SHOW_ADD_NOTE_FORM: 'show_add_note'
};

@inject('contentApprovalStore', 'progressStore')
@observer
class ContentListRow extends Component {
    constructor(props) {
        super(props);

        this.state = {
            activity_feed_state: ActivityFeedState.SHOW_FEED,
            showModal: false,
            showStoryModal: false,
            showManualDistributionModal: false
        };
    }

    // eslint-disable-next-line class-methods-use-this
    componentDidCatch(error) {
        console.error(error);
    }

    handleRequestFix = (event) => {
        event.preventDefault();
        this.setState({
            activity_feed_state: ActivityFeedState.SHOW_REQUEST_FIX_FORM
        });
    };

    handleCancelRequestFix = (event) => {
        event.preventDefault();
        this.setState({
            activity_feed_state: ActivityFeedState.SHOW_FEED
        });
    };

    handleAddNote = (event) => {
        event.preventDefault();
        this.setState({
            activity_feed_state: ActivityFeedState.SHOW_ADD_NOTE_FORM
        });
    };

    handleCancelAddNote = (event) => {
        event.preventDefault();
        this.setState({
            activity_feed_state: ActivityFeedState.SHOW_FEED
        });
    };

    handleSendRequestFix = async (feedback) => {
        await this.props.contentApprovalStore.handleSendRequestFix(feedback);
        this.setState({activity_feed_state: ActivityFeedState.SHOW_FEED});
    };

    handleSaveNote = async (event) => {
        event.preventDefault();

        await this.props.contentApprovalStore.handleSaveNote();
        this.setState({activity_feed_state: ActivityFeedState.SHOW_FEED});
    };

    handleUpdateContentStatus = async (event, desiredStatus) => {
        event.preventDefault();

        await this.props.contentApprovalStore.handleUpdateContentStatus(desiredStatus);
    };

    handleNotifyCreator = async (event) => {
        event.preventDefault();

        await this.props.contentApprovalStore.handleNotifyCreator();
    };

    handleResetBrandContentStatus = async (event) => {
        event.preventDefault();

        await this.props.contentApprovalStore.handleResetBrandContentStatus();
        await this.props.progressStore.refreshApprovalProgress();
    };

    handleRefresh = async (event) => {
        event.preventDefault();

        await this.props.contentApprovalStore.fetch();
    };

    handleReviewContentButtonClick = (event) => {
        event.preventDefault();

        this.props.tableProps.handleReviewContentButtonClick(
            this.props.contentApprovalStore.content
        );
    };

    /**
     * @param {React.MouseEvent<{}>} e
     * @memberof ContentListRow
     */
    toggleDistributionModalShown = (e) => {
        if (e) {
            e.preventDefault();
        }

        this.setState({showModal: !this.state.showModal});
        this.props.contentApprovalStore.fetch();
        this.props.progressStore.refreshApprovalProgress();
    };

    /**
     * @param {React.MouseEvent<{}>} e
     * @memberof ContentListRow
     */
    toggleStoryDistModalShown = (e) => {
        if (e) {
            e.preventDefault();
        }

        this.setState({showStoryModal: !this.state.showStoryModal});
        this.props.contentApprovalStore.fetch();
        this.props.progressStore.refreshApprovalProgress();
    };

    /**
     * @param {React.MouseEvent<{}>} e
     * @memberof ContentListRow
     */
    toggleManualDistributionModal = (e) => {
        if (e) {
            e.preventDefault();
        }

        this.setState({showManualDistributionModal: !this.state.showManualDistributionModal});
        this.props.contentApprovalStore.fetch();
        this.props.progressStore.refreshApprovalProgress();
    };

    getActivityFeedComponent() {
        const {content} = this.props.contentApprovalStore;
        const {editRequests} = content;
        const {activity_feed_state} = this.state;
        switch (activity_feed_state) {
            case ActivityFeedState.SHOW_FEED:
                return (
                    <div className="activity-section">
                        <ActivityFeedList
                            collapsed={false}
                            contentMetadata={content}
                            editRequests={editRequests || []}/>
                    </div>
                );
            case ActivityFeedState.SHOW_REQUEST_FIX_FORM:
                return (
                    <AddFeedbackForm
                        saveCallToAction="Request Fix"
                        handleCancel={this.handleCancelRequestFix}
                        handleSave={this.handleSendRequestFix}
                    />
                );
            case ActivityFeedState.SHOW_ADD_NOTE_FORM:
                return (
                    <AddFeedbackForm
                        saveCallToAction="Add Note"
                        handleCancel={this.handleCancelAddNote}
                        handleSave={this.handleSaveNote}
                    />
                );
            default:
                return (
                    <div className="activity-section">
                        <ActivityFeedList collapsed={false} contentMetadata={content} />
                    </div>
                );
        }
    }

    renderActions = () => {
        const {content} = this.props.contentApprovalStore;

        const {campaign_content_workflow, type: campaignPlatform} = window.sn.campaign;

        const menuOptions = [
            <MenuItem
                key="review-content"
                eventKey="review-content"
                onClick={(event) => this.handleReviewContentButtonClick(event)}
            >
                Review
            </MenuItem>,
            <MenuItem
                key="reload"
                eventKey="reload-content"
                onClick={(event) => this.handleRefresh(event)}
            >
                Reload
            </MenuItem>,
            <MenuItem
                key="manual-entry"
                eventKey="manual-entry"
                onClick={(event) => this.toggleManualDistributionModal(event)}
            >
                Manual Entry
            </MenuItem>,
            <MenuItem
                href={`/leads/${this.props.contentApprovalStore.content.lead_id}`}
                target="_blank"
                key="creator-details"
                eventKey="creator-details"
            >
                Creator
            </MenuItem>
        ];

        if (
            campaign_content_workflow === 'ops_and_brand_preapproval' &&
            ['brand_rejected_content', 'do_not_distribute', 'waiting_for_post'].includes(
                content.post_state
            )
        ) {
            menuOptions.push(
                <MenuItem
                    key="reset-brand-approval"
                    eventKey="reset-brand-approval"
                    onClick={(event) => this.handleResetBrandContentStatus(event)}
                >
                    Reset Brand Approval
                </MenuItem>
            );
        }

        if (
            [
                'waiting_for_post',
                'posted_for_admin',
                'admin_incorrect_post',
                'admin_rejected_post',
                'admin_correct_post',
                'brand_incorrect_post'
            ].includes(content.post_state)
                && (campaignPlatform === 'instagram' || campaignPlatform === 'tiktok')
        ) {
            menuOptions.push(
                <MenuItem
                    key="add-dist"
                    eventKey="add-dist"
                    onClick={(event) => this.toggleDistributionModalShown(event)}
                >
                    Add Distribution
                </MenuItem>
            );
        }

        if (
            [
                'waiting_for_post',
                'posted_for_admin',
                'admin_incorrect_post',
                'admin_rejected_post',
                'admin_correct_post',
                'brand_incorrect_post',
                'complete_post'
            ].includes(content.post_state)
                && (campaignPlatform === 'instagram' || campaignPlatform === 'tiktok')
        ) {
            menuOptions.push(
                <MenuItem
                    key="add-story-dist"
                    eventKey="add-story-dist"
                    onClick={event => this.toggleStoryDistModalShown(event)}
                >
                    {content.post_state === 'waiting_for_post'
                        ? 'Add Story Distribution'
                        : 'Edit Story Distribution'}
                </MenuItem>
            );
        }

        return (
            <DropdownButton
                disabled={this.props.contentApprovalStore.transmitting}
                className="notify-button warning"
                title="Actions"
                key="actions"
                id="actions-dropdown"
                noCaret
            >
                {menuOptions}
            </DropdownButton>
        );
    };

    render() {
        const {showModal, showStoryModal, showManualDistributionModal} = this.state;

        const {
            content_type,
            content_sn_type_id,
            content_sn_type,
            thumbnail_url,
            caption,
            content_metadata_id,
            social_user_id,
            username,
            post_state
        } = this.props.contentApprovalStore.content;

        const overlayClass = classNames('transmitting-overlay', {
            disabled: !this.props.contentApprovalStore.transmitting
        });

        const {type: campaignPlatform} = window.sn.campaign;

        const url = campaignPlatform === 'tiktok' ?
            `https://www.tiktok.com/@${username}` : `https://www.instagram.com/${username}`

        return (
            <div className="content-review-list-item">
                <div className={overlayClass} />
                <div
                    className={`content-section ${content_type}`}
                    style={{backgroundImage: `url(${thumbnail_url})`}}
                />
                <div className="details-section">
                    <div className="caption-section">
                        <div className="username-section">
                            <div className="profile-picture" />
                            <a
                                className="profile-link"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {username}
                            </a>
                        </div>
                        {content_sn_type ? (
                            <div className="content-creator-type">
                                <Tag
                                    data-content-sn-type-id={content_sn_type_id}
                                >
                                    {content_sn_type}
                                </Tag>
                            </div>
                        ) : null}
                        <EditCaption
                            metaDataId={content_metadata_id}
                            caption={caption}
                            preSubmit={this.props.contentApprovalStore.toggleTransmittingState}
                            postSubmit={this.props.contentApprovalStore.toggleTransmittingState}
                            showEditButton
                        />
                    </div>
                    {this.getActivityFeedComponent()}
                </div>
                <div className="status-section">
                    <div className="status-timeline">
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>The unique content ID.</Tooltip>}
                        >
                            <div className="timeline-node">
                                <div className="timeline-icon identifier" />
                                <div className="timeline-caption">{content_metadata_id}</div>
                            </div>
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{window.sn.maps.PostState[post_state]}</Tooltip>}
                        >
                            <div className="timeline-node">
                                <div className="timeline-icon state" />
                                <div className="timeline-caption">
                                    {toTitleCase(post_state.replace(/_/g, ' '))}
                                </div>
                            </div>
                        </OverlayTrigger>

                        {this.props.contentApprovalStore.content.has_distribution_on_campaign ? (
                            <a
                                className="timeline-node-link-wrapper"
                                rel="noopener noreferrer"
                                target="_blank"
                                href={this.props.contentApprovalStore.content.distributed_post_url}
                            >
                                <StatusTimelineItem
                                    className=""
                                    status="link"
                                    caption="View Post"
                                />
                            </a>
                        ) : null}

                        {this.renderActions()}

                        <AddStoryDistributionModal
                            {...this.props.contentApprovalStore.content}
                            campaignId={this.props.tableProps.campaign.id}
                            show={showStoryModal}
                            onToggleShown={this.toggleStoryDistModalShown}
                        />

                        <AddDistributionModal
                            {...this.props.contentApprovalStore.content}
                            campaignId={this.props.tableProps.campaign.id}
                            handleAddPotentialDistribution={
                                this.props.contentApprovalStore.handleAddPotentialDistribution
                            }
                            show={showModal}
                            onToggleShown={this.toggleDistributionModalShown}
                        />

                        <ManualDistributionModal
                            socialUserId={social_user_id}
                            contentMetadataId={content_metadata_id}
                            show={showManualDistributionModal}
                            onToggleShown={this.toggleManualDistributionModal}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default ContentListRow;



// WEBPACK FOOTER //
// ./approval_queues/components/content_review/list_view/content_row/list_row.js